<script setup lang="ts">
/**
 * Обёртка правового документа.
 *
 * Оферту и правила приёма платежей читают в двух состояниях: бегло перед
 * оплатой и въедливо, когда что-то пошло не так. Второе важнее — поэтому
 * узкая колонка, крупный межстрочный интервал, нумерованные пункты и якоря на
 * заголовках: на спорный пункт должно быть можно дать ссылку.
 */
import { RouterLink } from 'vue-router';

defineProps<{
  title: string;
  /** Дата редакции: документ без неё непригоден для спора о том, что изменилось. */
  revision: string;
  subtitle?: string;
}>();
</script>

<template>
  <div class="min-h-screen bg-white">
    <header class="border-b border-gray-200">
      <div class="mx-auto flex max-w-[46rem] items-center gap-3 px-6 py-4">
        <RouterLink to="/" class="text-[13px] font-semibold text-gray-900">Keypi</RouterLink>
        <span class="text-[13px] text-gray-400">·</span>
        <span class="text-[13px] text-gray-500">{{ title }}</span>
      </div>
    </header>

    <main class="mx-auto max-w-[46rem] px-6 py-10">
      <h1 class="text-[26px] font-semibold leading-tight text-gray-900">{{ title }}</h1>
      <p v-if="subtitle" class="mt-2 text-[14px] leading-relaxed text-gray-600">{{ subtitle }}</p>
      <p class="mt-3 text-[12.5px] text-gray-500">Редакция от {{ revision }}</p>

      <div class="legal mt-8">
        <slot />
      </div>

      <footer class="mt-12 flex flex-wrap gap-x-5 gap-y-2 border-t border-gray-200 pt-6 text-[12.5px]">
        <RouterLink to="/offer" class="text-indigo-600 hover:text-indigo-700">
          Публичная оферта
        </RouterLink>
        <RouterLink to="/payment-rules" class="text-indigo-600 hover:text-indigo-700">
          Правила приёма платежей
        </RouterLink>
        <RouterLink to="/privacy" class="text-indigo-600 hover:text-indigo-700">
          Политика обработки персональных данных
        </RouterLink>
        <RouterLink to="/" class="text-gray-500 hover:text-gray-700">На главную</RouterLink>
      </footer>
    </main>
  </div>
</template>

<style scoped>
/*
 * Типографика документа задаётся здесь, а не классами на каждом абзаце:
 * пунктов под сотню, и расставлять по ним одни и те же классы значит
 * гарантировать, что через месяц половина разъедется.
 */
.legal :deep(h2) {
  font-size: 17px;
  font-weight: 600;
  color: rgb(17 24 39);
  margin-top: 2.25rem;
  scroll-margin-top: 4rem;
}

.legal :deep(h3) {
  font-size: 14.5px;
  font-weight: 600;
  color: rgb(17 24 39);
  margin-top: 1.5rem;
}

.legal :deep(p),
.legal :deep(li) {
  font-size: 14px;
  line-height: 1.75;
  color: rgb(55 65 81);
}

.legal :deep(p) {
  margin-top: 0.75rem;
}

.legal :deep(ul),
.legal :deep(ol) {
  margin-top: 0.75rem;
  padding-left: 1.25rem;
  list-style: disc;
}

.legal :deep(ol) {
  list-style: decimal;
}

.legal :deep(li) {
  margin-top: 0.35rem;
}

.legal :deep(dl) {
  margin-top: 0.75rem;
  display: grid;
  gap: 0.35rem 1rem;
  grid-template-columns: minmax(9rem, auto) 1fr;
  font-size: 13.5px;
  line-height: 1.6;
}

.legal :deep(dt) {
  color: rgb(107 114 128);
}

.legal :deep(dd) {
  color: rgb(17 24 39);
}

.legal :deep(strong) {
  font-weight: 600;
  color: rgb(17 24 39);
}
</style>
